import React, { useEffect, useState, useCallback, useMemo } from "react"; 
import { useNavigate } from "react-router-dom";
import "./ProPaymentSuccess.css";
import Navbar from "../Navbar";

const BASE_URL =
  window.location.hostname === "localhost"
  ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";


const Countdown = React.memo(({ expiresAt }) => {
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0, hours: 0, minutes: 0, seconds: 0,
  });

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  useEffect(() => {
    if (!expiresAt) return;

    const calculateRemaining = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      let difference = expiry - now;

      if (difference <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      difference -= days * 24 * 60 * 60 * 1000;
      const hours = Math.floor(difference / (1000 * 60 * 60));
      difference -= hours * 60 * 60 * 1000;
      const minutes = Math.floor(difference / (1000 * 60));
      difference -= minutes * 60 * 1000;
      const seconds = Math.floor(difference / 1000);

      setTimeRemaining({ days, hours, minutes, seconds });
    };

    calculateRemaining();
    const timer = setInterval(calculateRemaining, 1000); 
    return () => clearInterval(timer);
  }, [expiresAt]);

  if (!expiresAt) return null;

  return (
    <div style={{ marginTop: "20px", padding: "20px", borderRadius: "16px", background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)" }}>
      <p><strong>Membership Status</strong></p>
      <p>🟢 Active</p>
      <p>💰 Plan: ₹29 / month</p>
      <p>📅 Valid Till: <strong>{formatDate(expiresAt)}</strong></p>

      <div style={{ marginTop: "18px", padding: "15px", borderRadius: "14px", background: "#ffffff", color: "#000" }}>
        <p style={{ marginBottom: "12px", fontWeight: "700" }}>⏳ Pro Access Remaining</p>
        <div style={{ display: "flex", justifyContent: "center", gap: "8px", flexWrap: "wrap" }}>
          <div><strong>{timeRemaining.days}</strong><small> Days</small></div>
          <div>:</div>
          <div><strong>{String(timeRemaining.hours).padStart(2, "0")}</strong><small> Hours</small></div>
          <div>:</div>
          <div><strong>{String(timeRemaining.minutes).padStart(2, "0")}</strong><small> Min</small></div>
          <div>:</div>
          <div><strong>{String(timeRemaining.seconds).padStart(2, "0")}</strong><small> Sec</small></div>
        </div>
      </div>
    </div>
  );
});

const ProPaymentSuccess = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("Creator");
  const [loadingUser, setLoadingUser] = useState(true);
  const [expiresAt, setExpiresAt] = useState(null);

 
  // The Pro menu now lives on ONE page — the TheBlogsSphere Pro Home Page,
  // which activation redirects to. This page stays as the payment receipt so
  // its URL keeps working, and hands the member over to it.
  const handleProHome = useCallback(() => navigate("/pro-home"), [navigate]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const savedUser = localStorage.getItem("user") || localStorage.getItem("currentUser");
        if (savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            const name = parsedUser?.name || parsedUser?.username || parsedUser?.fullName || parsedUser?.userName;
            if (name) setUserName(name);
          } catch (error) {
            console.log("Could not parse saved user");
          }
        }

        const token = localStorage.getItem("token");
        if (!token) {
          setLoadingUser(false);
          return;
        }

        const [userRes, proRes] = await Promise.all([
          fetch(`${BASE_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${BASE_URL}/api/pro-payment/status`, { headers: { Authorization: `Bearer ${token}` } })
        ]);

        if (userRes.ok) {
          const data = await userRes.json();
          const user = data?.user || data;
          const name = user?.name || user?.username || user?.fullName || user?.userName;
          if (name) setUserName(name);
        }

        if (proRes.ok) {
          const proData = await proRes.json();
          if (proData.success && proData.expiryDate) {
            setExpiresAt(proData.expiryDate);
          }
        }

      } catch (error) {
        console.error("User fetch error:", error);
      } finally {
        setLoadingUser(false);
      }
    };
    fetchUser();
  }, []);

  return (
    <div className="pro-success-page">
      <Navbar/>
      <div className="success-bg-circle circle-one"></div>
      <div className="success-bg-circle circle-two"></div>

      <div className="pro-success-card">
        <div className="success-icon">✓</div>
        <p className="success-small-heading">PAYMENT SUCCESSFUL</p>
        <h1>Welcome, {loadingUser? "Creator" : userName}! 🎉</h1>
        <p className="success-title">You are now a TheBlogsSphere Pro Member</p>
        <p className="success-description">
          Your payment has been successfully verified. Welcome to your Pro Creator Dashboard.
        </p>

   
        <Countdown expiresAt={expiresAt} />

        <div className="pro-home-actions">
          <h3>💎 Your Pro Creator Home</h3>
          <p className="pro-dashboard-description">
            Create content, manage your creator profile and your Pro account.
          </p>

          <br/>
          <div className="pro-action-grid">
            <button type="button" className="pro-action-card" onClick={handleProHome}>
              <span>💎</span><strong>Go to Pro Home</strong><small>TheBlogsSphere Pro Home Page</small>
            </button>
          </div>
        </div>

        <p className="success-note">Thank you for joining TheBlogsSphere Pro ❤️</p>
        <p className="success-security">🔒 Your payment was securely processed</p>
      </div>
    </div>
  );
};

export default ProPaymentSuccess;